import "./style.scss";

import { CheckOutlined } from "@mui/icons-material";
import { Autocomplete, Chip } from "@mui/material";
import ActionPlusSign from "assets/img/svg/actionPlusSign";
import Close from "assets/img/svg/close";
import type { TTags } from "components/Title";
import { tagsColors } from "constants/colors";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { createBeneficiaryTags, getBeneficiaryTags } from "store/beneficiaryStore";
import type { AppDispatch } from "store/store";
import { useAppSelector } from "store/store";
import { fetchTags, postNewTags } from "store/titleStore";
import { createTransfereeTag } from "store/transfereesStore";

import StyledTextField from "../StyledTextField/StyledTextField";

interface TagsProps {
    elementId: number;
    elementType: "titles" | "beneficiary" | "transferees";
    tags: TTags[] | null;
    onDelete: (i: number, tagName: string) => void;
}

const Tags = ({ elementId, elementType, tags, onDelete }: TagsProps) => {
    const [t] = useTranslation();
    const { id: idFromRoute } = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const id = elementType === "titles" ? String(elementId) : idFromRoute;
    const [keyCode, setKeyCode] = useState<null | string>(null);
    const [isShow, setShow] = useState(false);
    const [bufTags, setBufTags] = useState<TTags[] | null>(null);
    const { lastAddedTagId } = useAppSelector((state) => state[elementType]);
    const tagList = useAppSelector((state) =>
        elementType === "transferees" ? state[elementType].transfereesTags : state[elementType].tags,
    );
    const [tagName, setTagName] = useState<any>(null);

    const handleAddTag = (name: string, color: number) => {
        if (bufTags) {
            setBufTags((prev) => [...(prev as TTags[]), { id: 0, name, color }]);
        }
    };

    const handleEditTagId = (id: number) => {
        if (bufTags) {
            const tagToEdit = bufTags.find((tag) => tag.id === 0);
            if (tagToEdit === undefined) return;
            tagToEdit.id = id;
            // if i remove the three line above it doesn't work, no idea why, so let's keep it like this
            setBufTags((prev) => [...prev!.filter((item) => item.id !== 0)]);
        }
    };

    const handleDeleteTag = (id: number, tagName: string) => {
        if (bufTags) {
            setBufTags((prev) => prev!.filter((item) => item.id !== id));
            onDelete(id, tagName);
        }
    };

    const handleSave = useCallback(
        (currentTagName?: any) => {
            setShow(false);
            if (tagName === null || tagName === "") return;
            const tag = {
                name: currentTagName.name ? currentTagName.name.trim() : currentTagName.trim(),
                color: tagName.color ?? Math.floor(1 + Math.random() * 7),
            };
            const tagExistsOverall = tagList?.find((t) => t.name === tag.name);
            const tagExistsHere = bufTags?.find((t) => t.name === tag.name);
            if (!tagExistsOverall) {
                if (elementType === "titles") {
                    dispatch(postNewTags({ titleId: id, tag }));
                } else if (elementType === "beneficiary") {
                    dispatch(createBeneficiaryTags({ beneficiaryId: +id!, tags: tag, toast: true }));
                } else {
                    dispatch(createTransfereeTag({ data: { name: tag.name, color: tag.color, transfereeId: +id! } }));
                }
                handleAddTag(tag.name, tag.color);
            } else if (tagExistsOverall && !tagExistsHere) {
                if (elementType === "titles") {
                    dispatch(
                        postNewTags({
                            titleId: id,
                            tag: tagExistsOverall as { name: string; color: number },
                        }),
                    ).then((resp) => {
                        if (resp.meta.requestStatus === "fulfilled") dispatch(fetchTags());
                    });
                } else if (elementType === "beneficiary") {
                    dispatch(
                        createBeneficiaryTags({
                            beneficiaryId: +id!,
                            tags: tagExistsOverall as { name: string; color: number },
                            toast: true,
                        }),
                    ).then((resp) => {
                        if (resp.meta.requestStatus === "fulfilled") dispatch(getBeneficiaryTags());
                    });
                } else {
                    dispatch(createTransfereeTag({ data: { ...tagExistsOverall, transfereeId: +id! } }));
                }
                handleAddTag(tagExistsOverall.name, tagExistsOverall.color!);
            }
        },
        [tagList, tagName, elementType, handleAddTag, id],
    );

    useEffect(() => {
        const currentTagName = tagName?.name ? tagName.name : tagName;

        if (currentTagName === null || currentTagName === "") return;
        if (keyCode === "Enter" && currentTagName.trim() !== "") {
            handleSave(currentTagName);

            // Vivian had put this Timeout, I'm not sure why, but it caused a deleted tag to stay visible in the Autocomplete for a few seconds after being deleted.
            // setTimeout(() => {
            //   if (elementType === "titles") {
            //     dispatch(fetchTags());
            //   }
            //   else if(elementType === "beneficiary"){
            //     dispatch(getBeneficiaryTags());
            //   }
            // }, 1000);

            setTagName(null);
            setKeyCode(null);
        } else if (keyCode === "Escape") {
            setShow(false);
            setTagName(null);
            setKeyCode(null);
        }
    }, [dispatch, id, keyCode, setTagName, tagName, elementId, handleSave]);

    const handleClick = (tag?: any) => {
        handleSave(tag);
        setShow(false);
        setTagName(null);
    };

    useEffect(() => {
        setBufTags([]);
        setShow(false);
        setTagName(null);
    }, [id, setTagName]);

    useEffect(() => {
        if (tags) {
            setBufTags([...tags]);
        }
    }, [tags]);

    useEffect(() => {
        if (lastAddedTagId > -1) handleEditTagId(lastAddedTagId);
    }, [lastAddedTagId]);

    useEffect(() => {
        if (tagList === null) {
            if (elementType === "titles") {
                dispatch(fetchTags());
            } else if (elementType === "beneficiary") {
                dispatch(getBeneficiaryTags());
            }
        }
    }, [tagList, elementType, dispatch]);

    const renderTags = () => {
        return (bufTags || []).map((tag, i) => {
            const tagColor = tagsColors.find((item) => item.id === tag.color) || tagsColors[0];
            return (
                <Chip
                    key={`${tag.id}-${i}`}
                    size="small"
                    className={`tag-${tag.id}`}
                    label={tag.name}
                    onDelete={() => handleDeleteTag(tag.id, tag.name)}
                    sx={{
                        backgroundColor: tagColor?.backgroundColor,
                        color: tagColor?.textColor,
                    }}
                />
            );
        });
    };

    return (
        <div className="title-tags">
            {bufTags && bufTags.length ? <ul style={{ display: "flex", gap: "10px" }}>{renderTags()}</ul> : ""}
            {!isShow ? (
                // <Plus fill="#3C78F0" onClick={() => setShow((prev) => !prev)} />
                <Chip
                    size="small"
                    sx={{ backgroundColor: "white", border: "1px solid #EAEAEA" }}
                    onClick={() => setShow((prev) => !prev)}
                    label={
                        <div className="tags-add-button">
                            <ActionPlusSign />
                            <span className="tags-add-text">{t("Filters.Tag")}</span>
                        </div>
                    }
                />
            ) : (
                <Close className="title-tags-close" fill="#ad0e38" onClick={() => setShow((prev) => !prev)} />
            )}
            <div className="title-autocomplete">
                {isShow && tagList && (
                    <div className="autocomplete">
                        <Autocomplete
                            onKeyDown={(e) => setKeyCode(e.code)}
                            value={tagName}
                            onChange={(e, newValue: any) => setTagName(newValue)}
                            id={"tags-auto-complete"}
                            freeSolo
                            options={tagList}
                            getOptionLabel={(option) => option.name || option || ""}
                            renderOption={(props, option) => {
                                const tagColor = tagsColors.find((item) => item.id === option.color)!;
                                return (
                                    <li {...props} key={option.id}>
                                        <Chip
                                            size="small"
                                            label={option.name}
                                            sx={{
                                                backgroundColor: tagColor?.backgroundColor,
                                                color: tagColor?.textColor,
                                                cursor: "pointer",
                                            }}
                                        />
                                    </li>
                                );
                            }}
                            classes={{ popper: "tag-auto-complete-result" }}
                            renderInput={(params) => (
                                <StyledTextField
                                    {...params}
                                    onChange={(e) => setTagName(e.target.value)}
                                    size="small"
                                    placeholder={t("TagsFilter.TagName")}
                                    autoFocus={true}
                                />
                            )}
                        />
                        <div className="tags-auto-complete-btn">
                            <CheckOutlined sx={{ color: "#FFFFFF" }} onClick={() => handleClick(tagName)} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Tags;
